<template>
  <component :is="tag" v-bind="attrs" @click="$emit('click', $event)">
    <span v-if="loading" class="inline-flex items-center justify-center">
      <BaseIcon name="spinner" :size="spinnerSize" class="animate-spin" />
    </span>
    <span v-else-if="$slots['icon-left']" class="inline-flex items-center">
      <slot name="icon-left" />
    </span>
    <span :class="cn('inline-flex items-center gap-2', loading && 'opacity-0')">
      <slot />
    </span>
    <span v-if="!loading && $slots['icon-right']" class="inline-flex items-center">
      <slot name="icon-right" />
    </span>
  </component>
</template>

<script setup lang="ts">
import { computed, type Component } from 'vue';
import { RouterLink, type RouteLocationRaw } from 'vue-router';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';
import BaseIcon from '@/components/base/BaseIcon.vue';
import type { ButtonAs } from '@/types/component-types';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 rounded-md font-semibold whitespace-nowrap',
    'transition-[background-color,color,box-shadow,transform] duration-150 ease-[var(--ease-standard)]',
    'focus-visible:outline-none focus-visible:shadow-[var(--shadow-ring-focus)]',
    'disabled:cursor-not-allowed disabled:opacity-60',
  ],
  {
    variants: {
      variant: {
        primary:
          'bg-accent text-ink-inverse hover:bg-primary-400 active:bg-primary-600 hover:shadow-[var(--shadow-brand)]',
        secondary:
          'bg-paper text-ink border border-stroke hover:bg-paper-muted active:bg-paper-muted',
        ghost: 'bg-transparent text-accent hover:bg-accent-soft',
        'outline-inverse':
          'bg-transparent text-ink-inverse border border-paper hover:bg-paper hover:text-accent-strong active:bg-paper-muted active:text-accent-strong',
        danger: 'bg-danger-500 text-ink-inverse hover:bg-danger-700',
        link: 'bg-transparent text-accent px-0! h-auto! underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-9 px-3 text-sm',
        md: 'h-11 px-5 text-base',
        lg: 'h-14 px-7 text-lg',
      },
      iconOnly: {
        true: 'p-0',
        false: '',
      },
      block: {
        true: 'w-full',
        false: '',
      },
    },
    compoundVariants: [
      { iconOnly: true, size: 'sm', class: 'h-9 w-9 px-0 text-sm' },
      { iconOnly: true, size: 'md', class: 'h-11 w-11 px-0 text-base' },
      { iconOnly: true, size: 'lg', class: 'h-14 w-14 px-0 text-lg' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      iconOnly: false,
      block: false,
    },
  },
);

export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
export type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;
export type { ButtonAs } from '@/types/component-types';

interface Props {
  variant?: ButtonVariant;
  size?: ButtonSize;
  as?: ButtonAs;
  to?: RouteLocationRaw | null;
  href?: string | null;
  type?: 'button' | 'submit' | 'reset';
  loading?: boolean;
  disabled?: boolean;
  block?: boolean;
  iconOnly?: boolean;
  ariaLabel?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  as: 'button',
  to: null,
  href: null,
  type: 'button',
  loading: false,
  disabled: false,
  block: false,
  iconOnly: false,
  ariaLabel: null,
});

defineEmits<{
  (e: 'click', event: MouseEvent): void;
}>();

const isDisabled = computed(() => props.disabled || props.loading);

const tag = computed<string | Component>(() => {
  if (props.as === 'router-link') return RouterLink;
  if (props.as === 'a') return 'a';
  return 'button';
});

const classes = computed(() =>
  buttonVariants({
    variant: props.variant,
    size: props.size,
    iconOnly: props.iconOnly,
    block: props.block,
  }),
);

const attrs = computed(() => {
  const base: Record<string, unknown> = {
    class: classes.value,
    'aria-label': props.ariaLabel,
    'aria-busy': props.loading ? 'true' : undefined,
    'aria-disabled': props.disabled ? 'true' : undefined,
  };
  if (props.as === 'button') {
    base.type = props.type;
    base.disabled = isDisabled.value;
  } else if (props.as === 'a') {
    base.href = isDisabled.value ? undefined : props.href;
    base.role = 'button';
    if (isDisabled.value) base.tabindex = -1;
  } else {
    base.to = props.to;
    if (isDisabled.value) {
      base['aria-disabled'] = 'true';
      base.tabindex = -1;
    }
  }
  return base;
});

const spinnerSize = computed<16 | 20 | 24>(() => {
  if (props.size === 'sm') return 16;
  if (props.size === 'lg') return 24;
  return 20;
});
</script>
